import classNames from 'classnames';
import { createContext, forwardRef, ReactNode, useContext, useEffect, useRef, useState } from 'react';

import Icon from '../_HumanWritten/Icon/Icon';
import UnstyledButton from '../Button/UnstyledButton';

interface ExpanderContextProps {
	isOpen: boolean;
	collapse: () => void;
	expand: () => void;
	toggle: () => void;
	defaultOpen?: boolean;
}

const ExpanderContext = createContext<ExpanderContextProps>({
	isOpen: false,
	collapse: () => {},
	expand: () => {},
	toggle: () => {},
	defaultOpen: false,
});

const useExpanderContext = () => {
	return useContext(ExpanderContext);
};

interface ExpanderProps {
	children?: ReactNode;
}

function ExpanderContainer({ children }: ExpanderProps) {
	const [isOpen, setIsOpen] = useState(false);
	const value: ExpanderContextProps = {
		isOpen,
		collapse: () => setIsOpen(false),
		expand: () => setIsOpen(true),
		toggle: () => setIsOpen(prev => !prev),
	};
	return <ExpanderContext.Provider value={value}>{children}</ExpanderContext.Provider>;
}

interface ExpanderTitleProps {
	children?: ReactNode;
	className?: string;
	isDisabled?: boolean;
}

function ExpanderClickArea({ children, className, isDisabled }: ExpanderTitleProps) {
	const { toggle } = useExpanderContext();

	return (
		<UnstyledButton className={className} onClick={toggle} disabled={isDisabled}>
			{children}
		</UnstyledButton>
	);
}

interface ExpanderBodyProps extends React.HTMLAttributes<HTMLDivElement> {
	children?: React.ReactNode;
}

const ExpanderBody = forwardRef<HTMLDivElement, ExpanderBodyProps>(({ children, className, ...rest }, ref) => {
	const { isOpen } = useExpanderContext();

	const innerRef = useRef<HTMLDivElement>(null);
	const [contentHeight, setContentHeight] = useState(0);

	// Track the inner wrapper's natural height so the animated outer wrapper resizes
	// when content arrives late (loading spinner -> form), when the viewport changes
	// (responsive reflow), or when the user toggles fields that grow/shrink the body.
	// The inner wrapper sits inside `overflow: hidden`, but its scrollHeight reflects
	// the unclipped layout — so we get the right target even while collapsed.
	useEffect(() => {
		const el = innerRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return;

		const update = () => setContentHeight(el.scrollHeight);
		update();

		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	return (
		<div
			className={classNames('expander-body-wrapper', className)}
			style={{
				height: isOpen ? contentHeight : 0,
			}}
			{...rest}
		>
			<div ref={innerRef}>{children}</div>
		</div>
	);
});

function ExpanderIcon() {
	const { isOpen } = useExpanderContext();
	return <Icon name={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} useMaterialIcons />;
}

interface ExpandedLabelProps extends React.HTMLAttributes<HTMLDivElement> {
	children?: React.ReactNode;
}

const ExpandedLabel = forwardRef<HTMLDivElement, ExpandedLabelProps>(({ children, ...rest }, ref) => {
	const { isOpen } = useExpanderContext();

	if (isOpen) {
		return (
			<div ref={ref} {...rest}>
				{children}
			</div>
		);
	}

	return null;
});

const CollapsedLabel = forwardRef<HTMLDivElement, ExpandedLabelProps>(({ children, ...rest }, ref) => {
	const { isOpen } = useExpanderContext();

	if (isOpen) {
		return null;
	}

	return (
		<div ref={ref} {...rest}>
			{children}
		</div>
	);
});

type ExpanderComponents = {
	ClickArea: typeof ExpanderClickArea;
	Body: typeof ExpanderBody;
	Icon: typeof ExpanderIcon;
	CollapsedLabel: typeof CollapsedLabel;
	ExpandedLabel: typeof ExpandedLabel;
	useExpanderContext: typeof useExpanderContext;
};

export const Expander = Object.assign(ExpanderContainer, {
	ClickArea: ExpanderClickArea,
	Body: ExpanderBody,
	Icon: ExpanderIcon,
	CollapsedLabel,
	ExpandedLabel,
	useExpanderContext,
}) as typeof ExpanderContainer & ExpanderComponents;
